import { getCurrencyDecimals, getMoney } from '../price/utils';
import { isTaxAdded } from './tax';
const FORMULA_SCALE = 4;
// Mirrors the backend AdditionalFeeCalculator: integer minor units, HALF_UP once per amount, zero rows omitted.
export const estimateAdditionalFees = (fees, prices, currency, taxConfig) => {
    if (!fees?.length) {
        return [];
    }
    const scale = getCurrencyDecimals(currency);
    const toMinorUnits = (amount) => Math.round(amount * 10 ** scale);
    const toMoney = (minorUnits) => getMoney(minorUnits / 10 ** scale, currency);
    const base = sum(prices.map(price => toMinorUnits(price.amount)));
    const taxableBase = taxConfig
        ? sum(prices.filter(price => price.taxable).map(price => toMinorUnits(price.amount)))
        : 0;
    if (base <= 0) {
        return [];
    }
    return fees
        .map(fee => {
        const amount = getFeeAmount(fee.formula, base, scale);
        if (!amount) {
            return null;
        }
        const tax = taxConfig ? getFeeTax(taxConfig, divideHalfUp(amount * taxableBase, base)) : null;
        const gross = tax && isTaxAdded(taxConfig.type) ? amount + tax.amount : amount;
        return {
            name: fee.name,
            formula: fee.formula,
            amount: toMoney(gross),
            ...(tax && {
                tax: {
                    type: taxConfig.type,
                    name: taxConfig.name,
                    rate: taxConfig.rate,
                    taxable: toMoney(tax.taxable),
                    amount: toMoney(tax.amount),
                },
            }),
        };
    })
        .filter(Boolean);
};
export const sumAdditionalFees = (fees, pick) => sum((fees ?? []).map(fee => Number(pick(fee)?.amount ?? 0)));
const getFeeAmount = (formula, base, scale) => {
    if (formula?.fixedAmount) {
        return divideHalfUp(parseDecimal(formula.fixedAmount, FORMULA_SCALE), 10 ** (FORMULA_SCALE - scale));
    }
    if (formula?.percentage) {
        return divideHalfUp(base * parseDecimal(formula.percentage, 2), 100 * 10 ** 2);
    }
    return 0;
};
const getFeeTax = (taxConfig, taxableShare) => {
    if (!taxableShare) {
        return null;
    }
    const rate = parseDecimal(taxConfig.rate, FORMULA_SCALE);
    const percent = 100 * 10 ** FORMULA_SCALE;
    if (isTaxAdded(taxConfig.type)) {
        return { taxable: taxableShare, amount: divideHalfUp(taxableShare * rate, percent) };
    }
    const taxable = divideHalfUp(taxableShare * percent, percent + rate);
    return { taxable, amount: taxableShare - taxable };
};
const parseDecimal = (value, scale) => {
    const [whole, fraction = ''] = (value ?? '0').trim().split('.');
    return Number(whole || '0') * 10 ** scale + Number(fraction.padEnd(scale, '0').slice(0, scale));
};
const divideHalfUp = (numerator, denominator) => Math.floor((2 * numerator + denominator) / (2 * denominator));
const sum = (values) => values.reduce((total, value) => total + value, 0);
